'use client';

import * as React from 'react';
import { Heart } from 'lucide-react';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { cn } from '@/shared/utils/cn';

export interface LibraryColumn<T> {
  key: string;
  /** Header label; omit for icon-only / control columns. */
  header?: React.ReactNode;
  cell: (item: T) => React.ReactNode;
  /** Hide below md (keeps rows readable on mobile). */
  hideOnMobile?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

interface LibraryTableProps<T> {
  items: T[];
  columns: LibraryColumn<T>[];
  getRowId: (item: T) => string;
  rowTestId?: (item: T) => string;
  onRowClick?: (item: T) => void;
  favorite?: {
    isFavorite: (item: T) => boolean;
    onToggle: (item: T) => void;
    testId?: (item: T) => string;
    label?: (fav: boolean) => string;
  };
  /** Optional leading multi-select column (e.g. audio-stories batch play). */
  selection?: {
    isSelected: (item: T) => boolean;
    onToggle: (item: T) => void;
    canSelect?: (item: T) => boolean;
    headerChecked: boolean;
    onToggleAll: () => void;
    testId?: (item: T) => string;
  };
  /** Per-row trailing actions (edit/delete/…). Clicks are isolated from the row. */
  actions?: (item: T) => React.ReactNode;
  testId?: string;
}

const alignClass = { left: 'text-left', center: 'text-center', right: 'text-right' } as const;

/**
 * Shared library table used by every grid (stories, audio-stories, books) so they stay
 * visually and behaviourally consistent — same header style, favorite column, row-click,
 * trailing actions, and responsive column hiding. Domain differences live in `columns`.
 */
export function LibraryTable<T>({
  items,
  columns,
  getRowId,
  rowTestId,
  onRowClick,
  favorite,
  selection,
  actions,
  testId,
}: LibraryTableProps<T>) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid={testId}>
      <table className="w-full">
        <thead className="bg-muted border-b border-border">
          <tr>
            {selection && (
              <th className="w-10 px-2 py-3 text-center">
                <Checkbox checked={selection.headerChecked} onCheckedChange={() => selection.onToggleAll()} aria-label="select all" />
              </th>
            )}
            {favorite && <th className="w-10 px-2 py-3 text-center"><Heart className="w-3.5 h-3.5 mx-auto text-muted-foreground" /></th>}
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  'px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider',
                  alignClass[c.align ?? 'left'],
                  c.hideOnMobile && 'hidden md:table-cell',
                  c.className,
                )}
              >
                {c.header}
              </th>
            ))}
            {actions && <th className="w-28 px-2 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.map((item) => {
            const fav = favorite?.isFavorite(item) ?? false;
            return (
              <tr
                key={getRowId(item)}
                data-testid={rowTestId?.(item)}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                className={cn('bg-card transition-colors hover:bg-muted/40', onRowClick && 'cursor-pointer')}
              >
                {selection && (
                  <td className="w-10 px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {(selection.canSelect?.(item) ?? true) && (
                      <Checkbox
                        checked={selection.isSelected(item)}
                        onCheckedChange={() => selection.onToggle(item)}
                        data-testid={selection.testId?.(item)}
                        aria-label="select row"
                      />
                    )}
                  </td>
                )}
                {favorite && (
                  <td className="w-10 px-2 py-3 text-center">
                    <button
                      type="button"
                      data-testid={favorite.testId?.(item)}
                      aria-pressed={fav}
                      aria-label={favorite.label?.(fav)}
                      onClick={(e) => { e.stopPropagation(); favorite.onToggle(item); }}
                      className={cn('p-1.5 rounded-md transition-colors', fav ? 'text-primary' : 'text-muted-foreground hover:text-primary')}
                    >
                      <Heart className={cn('w-4 h-4', fav && 'fill-current')} />
                    </button>
                  </td>
                )}
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn('px-4 py-3 text-sm', alignClass[c.align ?? 'left'], c.hideOnMobile && 'hidden md:table-cell', c.className)}
                  >
                    {c.cell(item)}
                  </td>
                ))}
                {actions && (
                  <td className="w-28 px-2 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">{actions(item)}</div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
